import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Ticket,
  Profile2User,
  SearchNormal,
  Filter,
  Clock,
  TickCircle,
} from 'iconsax-react';
import { ColumnsType } from 'antd/es/table';

import {
  useGetSupportTicketsQuery,
  useGetSupportStatsQuery,
} from '../../store/services/SupportService';
import { SupportTicket } from '../../types/support';
import { useDebounce } from '../../hooks/useDebounce';

import Button from '../../components/atoms/Button';
import CustomTable from '../../components/atoms/CustomTable';
import Input from '../../components/atoms/Input';
import Chip from '../../components/atoms/Chip';
import { StatisticCard } from '../../components/atoms/StatisticCard';
import SectionTitle from '../../components/atoms/SectionTitle';
import SectionDesc from '../../components/atoms/SectionDesc';
import FilterModal from './FilterModal';

const SupportTicketsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL query parameters as single source of truth for filters
  const urlPage = searchParams.get('page');
  const currentPage = urlPage ? Math.max(0, parseInt(urlPage, 10) - 1) : 0;
  const statusIdsFilter =
    searchParams.get('statusIds') || searchParams.get('status') || '';
  const customerTypeFilter = searchParams.get('customerType') || '';
  const dateFromFilter =
    searchParams.get('dateFrom') || searchParams.get('fromDate') || '';
  const dateToFilter =
    searchParams.get('dateTo') || searchParams.get('toDate') || '';
  const searchFilter = searchParams.get('search') || '';

  const [searchText, setSearchText] = useState(searchFilter);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Synchronize local input state if URL search param changes externally
  useEffect(() => {
    setSearchText(searchFilter);
  }, [searchFilter]);

  // Debounce search text and sync to URL search params
  const debouncedSearch = useDebounce(searchText, 400);

  useEffect(() => {
    const currentSearch = searchParams.get('search') || '';
    if (debouncedSearch !== currentSearch) {
      const nextParams = new URLSearchParams(searchParams);
      if (debouncedSearch) {
        nextParams.set('search', debouncedSearch);
      } else {
        nextParams.delete('search');
      }
      nextParams.delete('page');
      setSearchParams(nextParams);
    }
  }, [debouncedSearch]);

  // Calculate applied filters count
  const appliedFiltersCount = [
    statusIdsFilter,
    customerTypeFilter,
    dateFromFilter,
    dateToFilter,
  ].filter(Boolean).length;

  const { data: statsData, isFetching: isLoadingStats } =
    useGetSupportStatsQuery(
      dateFromFilter || dateToFilter || statusIdsFilter || customerTypeFilter
        ? {
            statusIds: statusIdsFilter || undefined,
            dateFrom: dateFromFilter || undefined,
            dateTo: dateToFilter || undefined,
            customerType: customerTypeFilter || undefined,
          }
        : undefined
    );

  const statsPayload = (statsData?.payload || statsData) as any;

  const totalTickets =
    statsPayload?.allTicketsNumber ??
    statsPayload?.total ??
    statsPayload?.totalTickets ??
    statsPayload?.allTickets ??
    statsPayload?.all ??
    0;

  const newTickets =
    statsPayload?.newTicketsNumber ??
    statsPayload?.new ??
    statsPayload?.newTickets ??
    0;

  const inProgressTickets =
    statsPayload?.inProgressTicketsNumber ??
    statsPayload?.inProgress ??
    statsPayload?.inProgressTickets ??
    null;

  const closedTickets =
    statsPayload?.closedTicketsNumber ??
    statsPayload?.closed ??
    statsPayload?.closedTickets ??
    null;

  const hasExtendedStats =
    inProgressTickets !== null || closedTickets !== null;

  const { data: ticketsData, isFetching: isLoadingTickets } =
    useGetSupportTicketsQuery({
      page: currentPage,
      search: searchFilter || undefined,
      limit: 10,
      statusIds: statusIdsFilter || undefined,
      customerType: customerTypeFilter || undefined,
      dateFrom: dateFromFilter || undefined,
      dateTo: dateToFilter || undefined,
    });

  const tickets = ticketsData?.payload?.content || [];
  const totalElements = ticketsData?.payload?.totalElements || 0;

  const columns: ColumnsType<SupportTicket> = [
    {
      title: t('support.ticketId', 'Ticket ID'),
      dataIndex: 'serial',
      key: 'serial',
    },
    {
      title: t('support.ticketReason', 'Ticket reason'),
      dataIndex: 'reason',
      key: 'reason',
      render: (reason) => reason?.name || '---',
    },
    {
      title: t('support.name', 'Name'),
      dataIndex: 'customerName',
      key: 'customerName',
      render: (name) => name || '---',
    },
    {
      title: t('support.customerMail', 'Customer mail'),
      dataIndex: 'customerEmail',
      key: 'customerEmail',
      render: (customerEmail) => customerEmail || '---',
    },
    {
      title: t('support.agentName', 'Agent name'),
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      render: (assignedTo) => assignedTo || '---',
    },
    {
      title: t('support.ticketStatus', 'Ticket status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: { id: string; code: string; name: string }) => {
        let type: 'primary' | 'warning' | 'success' | 'default' = 'default';
        if (status?.code === 'NEW') type = 'primary';
        if (status?.code === 'IN_PROGRESS') type = 'warning';
        if (status?.code === 'CLOSED') type = 'success';
        return (
          <Chip type={type}>
            {status?.code
              ? t(`support.${status.code}`, status?.name || '---')
              : status?.name || '---'}
          </Chip>
        );
      },
    },
    {
      title: t('support.customerType', 'Customer type'),
      dataIndex: 'customerType',
      key: 'customerType',
      render: (customerType: string) => {
        const customerTypeOptions: Record<string, string> = {
          BUSINESS_BUYER: t(
            'support.customerTypes.business_buyer',
            'Business buyer'
          ),
          BUSINESS_SELLER: t(
            'support.customerTypes.business_seller',
            'Business seller'
          ),
          INDIVIDUAL_BUYER: t(
            'support.customerTypes.individual_buyer',
            'Individual buyer'
          ),
          INDIVIDUAL_SELLER: t(
            'support.customerTypes.individual_seller',
            'Individual seller'
          ),
        };
        return (
          <span>
            {customerTypeOptions[customerType] || customerType || '---'}
          </span>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <SectionTitle text={t('support.title', 'Support tickets')} />
        <SectionDesc
          text={t('support.subTitle', 'Display All Yad system Tickets')}
        />
      </div>

      {/* Summary Metric Cards */}
      {isLoadingStats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white p-6 rounded-lg shadow-xs flex items-center justify-start animate-pulse"
            >
              <div className="flex items-center gap-x-4">
                <div className="bg-gray-100 p-3 rounded-xl w-12 h-12" />
                <div className="flex flex-col gap-2">
                  <div className="h-4 w-28 bg-gray-100 rounded" />
                  <div className="h-7 w-16 bg-gray-200 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : hasExtendedStats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatisticCard
            title={t('support.totalTickets', 'Total Support Tickets')}
            value={totalTickets}
            Icon={Ticket}
          />
          <StatisticCard
            title={t('support.newTickets', 'New Tickets')}
            value={newTickets}
            Icon={Profile2User}
          />
          <StatisticCard
            title={t('support.inProgressTickets', 'In Progress Tickets')}
            value={inProgressTickets || 0}
            Icon={Clock}
          />
          <StatisticCard
            title={t('support.closedTickets', 'Closed Tickets')}
            value={closedTickets || 0}
            Icon={TickCircle}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatisticCard
            title={t('support.totalTickets', 'Total Support Tickets')}
            value={totalTickets}
            Icon={Ticket}
          />
          <StatisticCard
            title={t('support.newTickets', 'New Tickets')}
            value={newTickets}
            Icon={Profile2User}
          />
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {t('support.title', 'Support tickets')}
              </h2>
              <p className="text-gray-500 text-sm">
                {t('support.subTitle', 'Display All Yad system Tickets')}
              </p>
            </div>

            <div className="flex gap-3">
              <Input
                CustomIcon={<SearchNormal size={18} className="" />}
                placeholder={t('support.searchPlaceholder', 'Search here...')}
                className="w-64 !rounded !h-[46px]"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />

              <Button
                icon={<Filter size={18} />}
                variant={appliedFiltersCount > 0 ? 'dark' : 'outline'}
                className="border-light-input-border rounded !h-[46px]"
                onClick={() => setIsFilterOpen(true)}
              >
                {appliedFiltersCount > 0
                  ? `${t('support.filter', 'Filter')} (${appliedFiltersCount})`
                  : t('support.filter', 'Filter')}
              </Button>
            </div>
          </div>

          <CustomTable
            columns={columns}
            dataSource={tickets}
            rowKey="id"
            currentPage={currentPage}
            totalElements={totalElements}
            loading={isLoadingTickets}
            onPageChange={(page) => {
              const nextParams = new URLSearchParams(searchParams);
              if (page > 1) {
                nextParams.set('page', page.toString());
              } else {
                nextParams.delete('page');
              }
              setSearchParams(nextParams);
            }}
            onRowClick={(e, record) => {
              if (record?.id) {
                navigate(`/support-tickets/${record.id}`);
              }
            }}
          />
        </div>
      </div>

      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      />
    </div>
  );
};

export default SupportTicketsPage;
